<script lang="ts" setup>
import { computed, ref, watch } from "vue";
import ProductsModuleContent from "../productsModule/productsModuleContent.vue";
import Icon from "../../components/icon/icon.vue";
import { useProductPageStore } from "../../stores/productPageStore";
import { useCartStatusUpsell } from "../../composables/useUpsell";

const props = defineProps<{
  checkoutLink: string;
  displayName: string;
  cartStatusImage: string;
  userAuthenticated?: string;
  recommendationsHeading?: string;
  esalesKey: string;
  categoryName: string;
}>();

const productPageStore = useProductPageStore();
const dialogElement = ref();
const isUserAuthenticated = computed(() => props.userAuthenticated === "True");


watch(
  () => productPageStore.cartStatusIsOpen,
  (newValue, oldValue) => {
    if (!dialogElement.value || newValue === oldValue) return;

    newValue ? dialogElement.value.showModal() : dialogElement.value.close();
  },
);

const handleClickClose = () => {
  productPageStore.setCartStatusIsOpen(false);
};

const handleClickOutsideDialog = (event) => {
  // Close the dialog if the user clicks outside of the content area
  if (event.target === dialogElement.value) {
    handleClickClose();
  }
};

const variantKey = computed(() => {
  return props.esalesKey && props.esalesKey + productPageStore.size;
});

const cartStatusUpsellTrigger = computed(() => {
  return productPageStore.cartStatusIsOpen && variantKey ? "true" : "";
});

const { upsellModel, isLoading } = useCartStatusUpsell({
  variantKey, 
  categoryName: props.categoryName, 
  trigger: cartStatusUpsellTrigger,
});

</script>

<template>
  <dialog
    class="cart-status"
    aria-live="polite"
    :aria-hidden="!productPageStore.cartStatusIsOpen"
    role="dialog"
    ref="dialogElement"
    aria-labelledby="cart-status-heading"
    @click="(e) => handleClickOutsideDialog(e)"
  >
    <article class="cart-status__content">
      <span class="cart-status__message">{{ $t("addedToCart") }}</span>
      <div class="cart-status__product">
        <div class="cart-status__image">
          <picture>
            <source type="image/webp" :srcset="props.cartStatusImage" />
            <img
              alt=""
              width="auto"
              height="auto"
              loading="lazy"
              :src="props.cartStatusImage"
            />
          </picture>
        </div>
        <header class="cart-status__text">
          <div>
            <button
              class="btn btn--small btn--link btn--close cart-status__close"
              type="button"
              @click.prevent="handleClickClose"
              :title="$t('close')"
            >
              <Icon type="close" />
            </button>
            <h3 class="cart-status__heading">
              {{ productPageStore.displayName }}
            </h3>
            <p class="cart-status__desc">
              {{ $t("color") }}: {{ productPageStore.itemColor }} ({{
                productPageStore.itemMetaColor
              }})
              <br />
              <span> {{ $t("size") }}: {{ productPageStore.sizeText }} </span>
            </p>
          </div>
          <div class="cart-status__buttons">
            <div>
              <button
                class="btn btn--white cart-status__btn"
                @click.prevent="handleClickClose"
              >
                {{ $t("continueShopping") }}
              </button>
            </div>
            <div>
              <a
                :href="props.checkoutLink"
                :autofocus="true"
                @click="handleClickClose"
                class="btn btn--profile cart-status__btn u-trailer-half"
                >{{ $t("goToCheckout") }}</a
              >
            </div>
          </div>
        </header>
      </div>
      <div class="layout-product__related" v-for="(upsellItem, index) in upsellModel" :key="index">
          <ProductsModuleContent
            v-if="upsellItem.primaryList"
            :key="index"
            :heading="upsellItem.primaryList.label ?? props.recommendationsHeading ?? $t('maybeYouAlsoLike')"
            :search-result-recommend-list="upsellItem"
            :hide-colors="true"
            :hide-favorites="true"
            :hide-promotion="false"
            :hide-sizes="true"
            :track-view-item-list="true"
            :track-select-item="true"
            :is-loading-favorites="false"
            :user-authenticated="isUserAuthenticated"
            :item-list-id="`cartstatus-${upsellItem.primaryList.algorithm}`"
            :item-list-name="`cartstatus-${upsellItem.primaryList.algorithm}`"
            :upsell-algorithm="upsellItem.primaryList.algorithm"
            :is-loading="isLoading"
          />
      </div>
    </article>
  </dialog>
</template>
